import React, { useState, useEffect } from 'react';
import { Table, Typography, Select, Button, Input, Tooltip, Tag } from 'antd';
import { EyeOutlined, MessageOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Ticket, TicketStatus } from '../types';
import './FormResponsesComponents.scss';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EditableNote: React.FC<{
	ticket: Ticket;
	onSave: (ticketId: string, note: string) => void;
}> = ({ ticket, onSave }) => {
	const [note, setNote] = useState(ticket.note || '');
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		setNote(ticket.note || '');
	}, [ticket.note]);

	const handleSave = () => {
		if (note !== ticket.note) {
			onSave(ticket.id, note);
		}
		setIsEditing(false);
	};

	return isEditing ? (
		<TextArea
			value={note}
			onChange={(e) => setNote(e.target.value)}
			onBlur={handleSave}
			autoSize={{ minRows: 2, maxRows: 4 }}
			autoFocus
		/>
	) : (
		<div
			onClick={() => setIsEditing(true)}
			className="min-h-[32px] cursor-pointer whitespace-pre-wrap py-1.5"
		>
			{note || <Text type="secondary">Click to add a note...</Text>}
		</div>
	);
};

interface FormResponseTableProps {
	formGroups: {
		id: string;
		title: string;
		questions: string[];
		responses: Ticket[];
	}[];
	onStatusChange: (ticketId: string, status: TicketStatus) => void;
	onNoteSave: (ticketId: string, note: string) => void;
	onViewDetails: (ticket: Ticket) => void;
	onViewCallDetails: (ticket: Ticket) => void;
}

export const FormResponseTable: React.FC<FormResponseTableProps> = ({
	formGroups,
	onStatusChange,
	onNoteSave,
	onViewDetails,
	onViewCallDetails,
}) => {
	const formatDate = (dateInMs: number) =>
		new Date(dateInMs).toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});

	const statusConfig: { [key in TicketStatus]: { label: string; color: string } } = {
		OPEN: { label: 'Open', color: 'blue' },
		IN_PROGRESS: { label: 'In Progress', color: 'gold' },
		COMPLETED: { label: 'Completed', color: 'green' },
	};

	const sharedColumns: TableProps<Ticket>['columns'] = [
		{
			title: 'Date',
			dataIndex: 'callDate',
			key: 'callDate',
			width: 220,
			render: (date: number) => formatDate(date),
			sorter: (a, b) => a.callDate - b.callDate,
		},
		{
			title: 'Caller',
			dataIndex: 'callerPhone',
			key: 'callerPhone',
			width: 150,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 170,
			render: (status: TicketStatus, record: Ticket) => (
				<Select
					value={status}
					onChange={(newStatus) => onStatusChange(record.id, newStatus)}
					className="w-full"
				>
					{Object.entries(statusConfig).map(([key, { label }]) => (
						<Option key={key} value={key}>
							{label}
						</Option>
					))}
				</Select>
			),
		},
		{
			title: 'Note',
			dataIndex: 'note',
			key: 'note',
			width: 250,
			render: (note: string, record: Ticket) => (
				<EditableNote ticket={record} onSave={onNoteSave} />
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 150,
			render: (_, record: Ticket) => (
				<div className="action-buttons-container">
					<Tooltip title="View Full Details">
						<Button
							type="primary"
							icon={<EyeOutlined />}
							onClick={() => onViewDetails(record)}
							className="bg-indigo-500 border-indigo-500"
						/>
					</Tooltip>
					<Tooltip title="View Call Details">
						<Button
							type="primary"
							icon={<MessageOutlined />}
							onClick={() => onViewCallDetails(record)}
							className="bg-indigo-500 border-indigo-500"
						/>
					</Tooltip>
				</div>
			),
		},
	];

	if (formGroups.length === 0) {
		return (
			<div className="empty-section-placeholder p-10">
				<p>No tickets to display in table view.</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-8">
			{formGroups.map((group) => {
				const questionColumns: TableProps<Ticket>['columns'] = group.questions.map((q) => ({
					title: q,
					key: q,
					render: (_, record: Ticket) => {
						const responseItem = record.responses.find((res) => res.question === q);
						return responseItem ? responseItem.answer : '—';
					},
				}));

				const statusColumn = {
					title: 'Status',
					dataIndex: 'status',
					key: 'status',
					width: 140,
					render: (status: TicketStatus) => (
						<Tag color={statusConfig[status].color} className="font-medium">
							{statusConfig[status].label.toUpperCase()}
						</Tag>
					),
				};

				const columnsWithStatusTag = [
					statusColumn,
					...sharedColumns.slice(0, 2),
					...questionColumns,
					...sharedColumns.slice(2),
				];

				return (
					<div key={group.id} className="form-group-container">
						<Typography.Title level={4} className="form-group-title">
							{group.title} ({group.responses.length} Responses)
						</Typography.Title>
						<Table
							columns={columnsWithStatusTag}
							dataSource={group.responses}
							rowKey="id"
							pagination={{ pageSize: 10, hideOnSinglePage: true, simple: true }}
							scroll={{ x: 'max-content' }}
							rowClassName={(record) =>
								record.status === 'COMPLETED' ? 'completed-row' : 'active-row'
							}
						/>
					</div>
				);
			})}
		</div>
	);
};
